import React from "react";
import type { ChartComponentProps } from "./types";
import { ChartContainer } from "./ChartContainer";
import { ChartTitle } from "./ChartTitle";
import { useChartAnimation } from "./useChartAnimation";
import { formatChartValue, useAvailableWidth } from "./chartUtils";
import { getChartColor } from "./chartColors";

const MIN_DONUT_SIZE = 120;
const MAX_DONUT_SIZE = 330;
const DONUT_THICKNESS_RATIO = 56 / 330;
const MIN_GAP = 16;
const MAX_GAP = 48;
const MIN_LEGEND_WIDTH = 140;
const MIN_W = 500;

/** Donut composition segments are always rendered as share percentages. */
const DONUT_VALUE_UNIT = "%";

export const DonutCompositionChart: React.FC<ChartComponentProps> = ({
  chartData,
  ctx,
}) => {
  const { S } = ctx;
  const anim = useChartAnimation(ctx, chartData);
  const rawSegments = chartData.segments || [];
  const baseSegments = rawSegments.map((s) => ({
    ...s,
    value: Math.max(0, s.value),
  }));
  const EPS = 1e-6;
  const baseTotal = baseSegments.reduce((sum, s) => sum + s.value, 0);
  const missingPercent = Math.max(0, 100 - baseTotal);
  const shouldAppendUnknown = missingPercent > EPS;
  const segments = shouldAppendUnknown
    ? [...baseSegments, { label: "Unknown", value: missingPercent }]
    : baseSegments;
  const [measureRef, availPx] = useAvailableWidth();

  if (segments.length === 0) {
    return null;
  }

  const total = segments.reduce((sum, s) => sum + s.value, 0);

  const availDesign = Math.max(
    MIN_W,
    availPx > 0 ? Math.round(availPx / S) : MIN_W,
  );

  const legendWidth = Math.max(
    MIN_LEGEND_WIDTH,
    Math.round(availDesign * 0.42),
  );
  const gap = Math.max(
    MIN_GAP,
    Math.min(MAX_GAP, Math.round(availDesign * 0.05)),
  );
  const donutSize = Math.max(
    MIN_DONUT_SIZE,
    Math.min(MAX_DONUT_SIZE, availDesign - legendWidth - gap),
  );
  const donutThickness = Math.round(donutSize * DONUT_THICKNESS_RATIO);

  const cx = donutSize / 2;
  const cy = donutSize / 2;
  const outerR = donutSize / 2;
  const innerR = outerR - donutThickness;

  const buildArcPath = (startAngle: number, endAngle: number, r: number) => {
    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle);
    const y2 = cy + r * Math.sin(endAngle);
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
    return { x1, y1, x2, y2, largeArc };
  };

  const segmentPaths: Array<{ d: string; color: string }> = [];
  let currentAngle = -Math.PI / 2;
  const GAP_ANGLE = 0.03;

  segments.forEach((seg, i) => {
    const pct = seg.value / total;
    const sweepAngle = pct * Math.PI * 2 - GAP_ANGLE;
    if (sweepAngle <= 0) {
      return;
    }

    const startAngle = currentAngle + GAP_ANGLE / 2;
    const endAngle = startAngle + sweepAngle;

    const outer = buildArcPath(startAngle, endAngle, outerR);
    const inner = buildArcPath(startAngle, endAngle, innerR);

    const d = [
      `M ${outer.x1} ${outer.y1}`,
      `A ${outerR} ${outerR} 0 ${outer.largeArc} 1 ${outer.x2} ${outer.y2}`,
      `L ${inner.x2} ${inner.y2}`,
      `A ${innerR} ${innerR} 0 ${inner.largeArc} 0 ${inner.x1} ${inner.y1}`,
      `Z`,
    ].join(" ");

    segmentPaths.push({ d, color: getChartColor(i) });
    currentAngle += pct * Math.PI * 2;
  });

  return (
    <ChartContainer ctx={ctx} chartData={chartData} align="flex-start">
      <div ref={measureRef} style={{ width: "100%" }}>
        {chartData.label && (
          <ChartTitle label={chartData.label} unit={DONUT_VALUE_UNIT} S={S} />
        )}

        <div className="flex gap-6 flex-center w-full">
          <svg
            viewBox={`0 0 ${donutSize} ${donutSize}`}
            style={{ flex: 1, flexShrink: 0 }}
          >
            {segmentPaths.map((seg, i) => {
              const stagger = anim.stagger(i, 0.08);
              return (
                <path
                  key={i}
                  d={seg.d}
                  fill={seg.color}
                  opacity={stagger.opacity}
                  transform={`rotate(${(1 - stagger.progress) * -10}, ${cx}, ${cy})`}
                />
              );
            })}
          </svg>

          <div className="flex flex-col flex-1 overflow-hidden">
            {segments.map((seg, i) => {
              const valueLabel = formatChartValue(seg.value, DONUT_VALUE_UNIT);
              return (
                <div
                  key={i}
                  className="flex items-center justify-between px-2 py-1"
                >
                  <div
                    className="flex gap-2 before:content-[''] before:block before:size-2 before:rounded-full before:bg-[var(--before-bg-color)] before:shrink-0 body_s text-content_primary"
                    style={{
                      "--before-bg-color": getChartColor(i),
                    }}
                  >
                    <span className="truncate max-w-20 md:max-w-38">
                      {seg.label}
                    </span>
                  </div>
                  <span className="body_s text-content_primary">
                    {valueLabel}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </ChartContainer>
  );
};
